import React, { useState, useEffect } from 'react';
import { fetchFeesData, americanGrades, britishGrades, schools } from '../../../utils/feesApi';
import './FeesCalculator.css'; // Corrected import path

const FeesCalculator = () => {
    const [selectedSchool, setSelectedSchool] = useState('');
    const [selectedGradeId, setSelectedGradeId] = useState('');
    const [feesInfo, setFeesInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (selectedSchool && selectedGradeId) {
            setLoading(true);
            setError(null);
            fetchFeesData(selectedSchool, selectedGradeId)
                .then(data => {
                    setFeesInfo(data);
                })
                .catch(err => {
                    setError('Error loading fees information. Please try again.');
                    console.error('Error fetching fees data:', err);
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            setFeesInfo(null);
        }
    }, [selectedSchool, selectedGradeId]);

    const handleSelectSchool = (school) => {
        setSelectedSchool(school);
        setSelectedGradeId(''); // Clear grade selection when school changes
        setFeesInfo(null); // Clear fees display
    };

    const handleSelectGrade = (gradeId) => {
        setSelectedGradeId(gradeId);
    };

    const viewFees = () => {
        // Data fetching is already handled by useEffect when selectedSchool and selectedGradeId change
        if (!selectedSchool || !selectedGradeId) {
            setError('Please select both a school and a grade/year group to view fees.');
            return;
        }
    };

    const printFees = () => {
        const content = document.getElementById('fees-display').innerHTML;
        const printWindow = window.open('', '', 'height=800,width=1000');
        printWindow.document.write(`
            <html>
            <head>
                <title>Print Fees</title>
                <style>
                    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding:20px; }
                    .fees-actions { display: none; }
                    table { width:100%; border-collapse: collapse; }
                    th, td { border:1px solid #ddd; padding:8px; text-align:center; }
                    th { background:#f9f9f9; }
                    .total-row { background:#e8f4ff; font-weight:bold; }
                    .fees-summary, .payment-notes { margin-top:20px; padding:15px; border:1px solid #ddd; border-radius:6px; }
                </style>
            </head>
            <body>${content}</body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    };

    return (
        <div className="fees-calculator-container">
            <div className="selection-panel">
                <h2 className="panel-title">Select School:</h2>
                <div className="school-buttons">
                    {Object.entries(schools).map(([key, name]) => (
                        <div
                            key={key}
                            className={`school-btn ${selectedSchool === key ? 'active' : ''}`}
                            onClick={() => handleSelectSchool(key)}
                        >
                            <h3>{name}</h3>
                        </div>
                    ))}
                </div>

                <div className="selection-group">
                    <label className="selection-label">Select Grade/Year Group:</label>
                    <div className="grade-selector"
                         style={{ display: selectedSchool === 'american' ? 'grid' : 'none' }}>
                        {Object.entries(americanGrades).map(([id, name]) => (
                            <div
                                key={id}
                                className={`grade-btn ${parseInt(selectedGradeId) === parseInt(id) ? 'active' : ''}`}
                                onClick={() => handleSelectGrade(parseInt(id))}
                                data-grade-id={id}
                            >
                                {name}
                            </div>
                        ))}
                    </div>

                    <div className="grade-selector"
                         style={{ display: selectedSchool === 'british' ? 'grid' : 'none' }}>
                        {Object.entries(britishGrades).map(([id, name]) => (
                            <div
                                key={id}
                                className={`grade-btn ${parseInt(selectedGradeId) === parseInt(id) ? 'active' : ''}`}
                                onClick={() => handleSelectGrade(parseInt(id))}
                                data-grade-id={id}
                            >
                                {name}
                            </div>
                        ))}
                    </div>
                </div>

                <button
                    type="button"
                    className="submit-btn"
                    onClick={viewFees}
                    disabled={!selectedSchool || !selectedGradeId}
                >
                    <i className="fas fa-eye"></i> View Fees
                </button>
            </div>

            {loading && (
                <div className="loading" id="loading" style={{ display: loading ? 'block' : 'none' }}>
                    <div className="loading-spinner"></div>
                    <p>Loading fees information...</p>
                </div>
            )}

            {error && (
                <div className="fees-display" id="fees-display" style={{ display: 'block' }}>
                    <div className="error-message">
                        <i className="fas fa-exclamation-circle" style={{ fontSize: '48px', marginBottom: '15px' }}></i>
                        <h3>{error}</h3>
                    </div>
                </div>
            )}

            {feesInfo && !loading && !error && (
                <div className="fees-display" id="fees-display" style={{ display: 'block' }}>
                    <div className="fees-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                        <div>
                            <h2 className="fees-title">{feesInfo.gradeName} Fees Structure</h2>
                            <p className="fees-subtitle">{feesInfo.schoolName} - Academic Year 2025-2026</p>
                        </div>
                        <div className="fees-actions" style={{ marginTop: '10px' }}>
                            <button onClick={printFees} style={{ background: '#27ae60', color: 'white', border: 'none', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer', marginRight: '10px' }}>
                                <i className="fas fa-print"></i> Print
                            </button>
                        </div>
                    </div>

                    <style>
                        {`
                        @media print {
                            .fees-actions { display:none !important; }
                        }
                        `}
                    </style>

                    <div className="table-responsive">
                        <table className="fees-table">
                            <thead>
                                <tr>
                                    <th>Inst.No</th>
                                    <th>Inst.Name</th>
                                    <th>Due Date</th>
                                    <th>Deadline Date</th>
                                    <th>Edu Fees</th>
                                    <th>Act</th>
                                    <th>Books</th>
                                    <th>Add. Fees</th>
                                    <th>Tot. Without Bus</th>
                                    <th>Bus Fees</th>
                                    <th>Total Fees</th>
                                </tr>
                            </thead>
                            <tbody>
                                {feesInfo.feesData.map((fee, index) => (
                                    <tr key={index}>
                                        <td data-label="Inst.No">{fee.termno}</td>
                                        <td data-label="Inst.Name">{fee.termname}</td>
                                        <td data-label="Due Date">{new Date(fee.duedt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, ' ')}</td>
                                        <td data-label="Deadline">{new Date(fee.deadlinedt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, ' ')}</td>
                                        <td data-label="Edu Fees">{fee.edufees.toLocaleString()}</td>
                                        <td data-label="Act">{fee.actfees.toLocaleString()}</td>
                                        <td data-label="Books">{fee.bksfees.toLocaleString()}</td>
                                        <td data-label="Add. Fees">{fee.addfees.toLocaleString()}</td>
                                        <td data-label="Tot. Without Bus">{fee.totfees.toLocaleString()}</td>
                                        <td data-label="Bus Fees">{fee.busfees.toLocaleString()}</td>
                                        <td data-label="Total Fees">{fee.gtot.toLocaleString()}</td>
                                    </tr>
                                ))}
                                <tr className="total-row">
                                    <td colSpan="4">Total</td>
                                    <td>{feesInfo.feesData.reduce((sum, fee) => sum + fee.edufees, 0).toLocaleString()}</td>
                                    <td>{feesInfo.feesData.reduce((sum, fee) => sum + fee.actfees, 0).toLocaleString()}</td>
                                    <td>{feesInfo.feesData.reduce((sum, fee) => sum + fee.bksfees, 0).toLocaleString()}</td>
                                    <td>{feesInfo.feesData.reduce((sum, fee) => sum + fee.addfees, 0).toLocaleString()}</td>
                                    <td>{feesInfo.totalWithoutBus.toLocaleString()}</td>
                                    <td>{feesInfo.totalBusFees.toLocaleString()}</td>
                                    <td>{feesInfo.grandTotal.toLocaleString()}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="fees-summary">
                        <h3>Payment Summary</h3>
                        <div className="summary-item">
                            <span>Total Fees:</span>
                            <span>EGP {feesInfo.totalWithoutBus.toLocaleString()}</span>
                        </div>
                        <div className="summary-item">
                            <span>Bus Fees:</span>
                            <span>EGP {feesInfo.totalBusFees.toLocaleString()}</span>
                        </div>
                        <div className="summary-item summary-total">
                            <span>Total Payable:</span>
                            <span>EGP {feesInfo.grandTotal.toLocaleString()}</span>
                        </div>
                    </div>

                    {feesInfo.paymentNotes && feesInfo.paymentNotes.length > 0 && (
                        <div className="payment-notes">
                            <h3>Payment Notes</h3>
                            <ul>
                                {feesInfo.paymentNotes.map((note, index) => (
                                    <li key={index} dangerouslySetInnerHTML={{ __html: note }}></li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default FeesCalculator;
